import { CheckInput, Table, TextOutlinedInput } from '@octano/global-ui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { Currency } from '../../../../types/tariff';
import {
  formatToOnlyPositiveIntegers,
  TariffFormatter,
} from '../../../../utils/currency';
import { AcademicOfferFinantialConfig } from '../types';

type AcademicOfferRequirementsFinantialProps = {
  errors?: any[];
  configs?: AcademicOfferFinantialConfig[];
  currency?: Currency;
  onRemoveConfig?: (index: number) => void;
  onUpdateConfig?: (index: number, value: AcademicOfferFinantialConfig) => void;
  onAddConfig?: (item: AcademicOfferFinantialConfig) => void;
};

export function AcademicOfferRequirementsFinantial({
  configs = [],
  errors = [],
  currency,
  onAddConfig,
  onUpdateConfig,
  onRemoveConfig,
}: AcademicOfferRequirementsFinantialProps) {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const { rulesPatternDecimal } = useValidations();

  const handleAddConfig = useCallback(
    (code: string) => {
      !!onAddConfig &&
        onAddConfig({
          code,
          value: 0,
        });
    },
    [onAddConfig],
  );

  const handleRemoveConfig = useCallback(
    (code: string) => {
      const configIndex = configs.findIndex((c) => c.code === code);
      !!(onRemoveConfig && configIndex !== -1) && onRemoveConfig(configIndex);
    },
    [onRemoveConfig, configs],
  );

  const handleChangeConfig = useCallback(
    (code: string, value: number) => {
      const configIndex = configs.findIndex((c) => c.code === code);

      if (configIndex === -1 || !onUpdateConfig) {
        return;
      }
      onUpdateConfig(configIndex, { code, value });
    },
    [onUpdateConfig, configs],
  );

  return (
    <div>
      <Table
        columns={[
          {
            columnName: 'item',
            thClassName: 'text-center',
            tdClassName: 'text-center',
            headerText: t(`requirements.financial.table.item`),
          },
          {
            columnName: 'requiresPayment',
            thClassName: 'text-center',
            tdClassName: 'text-center',
            headerText: t(`requirements.financial.table.requiresPayment`),
            cellFormat({ row }) {
              return (
                <>
                  <CheckInput
                    name={`requiresPayment.${row.code}`}
                    label=""
                    value={!!configs.some((c) => c.code === row.code)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleAddConfig(row.code);
                      } else {
                        handleRemoveConfig(row.code);
                      }
                    }}
                  />
                </>
              );
            },
          },
          {
            columnName: 'value',
            thClassName: 'text-center',
            tdClassName: 'text-center',
            headerText: t(`requirements.financial.table.value`),
            width: '150px',
            cellFormat({ row, index }) {
              const config = configs.find((c) => c.code === row.code);
              return (
                <>
                  <TextOutlinedInput
                    name={`value.${row.code}`}
                    placeholder="0"
                    rules={{
                      pattern: rulesPatternDecimal,
                    }}
                    value={config?.value ?? 0}
                    defaultValue={TariffFormatter(config?.value ?? 0, currency)}
                    formatter={(value) => TariffFormatter(value, currency)}
                    disabled={!config}
                    onChange={(e) => {
                      const value = formatToOnlyPositiveIntegers(
                        e.target.value,
                      );
                      handleChangeConfig(row.code, Number(value));
                    }}
                    errorText={
                      errors?.find(
                        (e) =>
                          e?.value?.ref?.name === `finantials.${index}.value`,
                      )?.value?.message
                    }
                  />
                </>
              );
            },
          },
        ]}
        data={[
          {
            code: 'tuition',
            item: t(`requirements.financial.table.tuitionLabel`),
          },
          {
            code: 'fee',
            item: t(`requirements.financial.table.feeLabel`),
          },
          {
            code: 'certificate',
            item: t(`requirements.financial.table.certificateLabel`),
          },
        ]}
      />
    </div>
  );
}
